import { t } from "../lib/i18n";
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PublicNavbar from './PublicNavbar';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: 'General Support',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    
    setSending(true);
    const targetEmail = formData.subject === 'Enterprise Sales' ? 'sales@credominion.com' : 'support@credominion.com';
    const subject = `[CRE Dominion - ${formData.subject}] Inquiry from ${formData.name}`;
    const body = `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company || 'N/A'}\nInquiry Type: ${formData.subject}\n\nMessage:\n${formData.message}`;
    const mailtoUrl = `mailto:${targetEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    fetch(`https://formsubmit.co/ajax/${targetEmail}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        company: formData.company || 'N/A',
        subject: formData.subject,
        message: formData.message,
        _subject: subject,
        _captcha: 'false'
      })
    })
    .catch(() => {})
    .finally(() => {
      setSending(false);
      setSubmitted(true);
      try {
        window.location.href = mailtoUrl;
      } catch {}
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <Helmet>
        <title>{t("contact.k1")}</title>
        <meta name="description" content="Get in touch with the CRE Dominion advisory and technical support team. Inquire about AI lease abstraction, property valuation, or enterprise platform integrations." />
        <link rel="canonical" href="https://credominion.com/contact" />
        <meta property="og:title" content="Contact CRE Dominion | Commercial Real Estate Platform Support" />
        <meta property="og:description" content="Reach the CRE Dominion team for technical support, enterprise deployments, and commercial real estate analytics partnerships." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact CRE Dominion",
            "url": "https://credominion.com/contact",
            "mainEntity": {
              "@type": "Organization",
              "name": "CRE Dominion",
              "url": "https://credominion.com",
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "email": "support@credominion.com",
                  "contactType": "Customer Support",
                  "availableLanguage": ["English"]
                },
                {
                  "@type": "ContactPoint",
                  "email": "sales@credominion.com",
                  "contactType": "Sales",
                  "availableLanguage": ["English"]
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <PublicNavbar />

      <main className="max-w-5xl mx-auto px-4 py-12 md:py-16">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t("contact.k4")}</span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mt-2 mb-4">
            {t("contact.k2")} </h1>
          <p className="text-lg text-gray-600">
            {t("contact.k5")} </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Details & Info Cards */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
              <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 mb-4 font-bold">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{t("contact.k6")}</h3>
              <p className="text-sm text-gray-500 mb-3">{t("contact.k7")}</p>
              <a href="mailto:support@credominion.com" className="text-blue-600 hover:text-blue-800 font-semibold text-sm break-all">
                support@credominion.com
              </a>
              <div className="mt-2 text-xs text-gray-400 font-medium">{t("contact.k8")}</div>
            </div>
          </div>

          {/* Interactive Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                    ✓
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t("contact.k9")}</h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6">
                    {t("contact.k10")} <strong>{formData.subject === 'Enterprise Sales' ? 'sales@credominion.com' : 'support@credominion.com'}</strong>{t("contact.k11")} </p>
                  <div className="flex justify-center gap-4 flex-wrap">
                    <a
                      href={`mailto:${formData.subject === 'Enterprise Sales' ? 'sales@credominion.com' : 'support@credominion.com'}?subject=${encodeURIComponent(`[CRE Dominion - ${formData.subject}] Inquiry from ${formData.name}`)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company || 'N/A'}\nInquiry Type: ${formData.subject}\n\nMessage:\n${formData.message}`)}`}
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors text-sm inline-flex items-center gap-2"
                    >
                      {t("contact.k12")} </a>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: '', email: '', company: '', subject: 'General Support', message: '' });
                      }}
                      className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors text-sm"
                    >
                      {t("contact.k13")} </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{t("contact.k14")}</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                        {t("contact.k15")} </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={t("contact.k16")}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                        {t("contact.k17")} </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="sarah@commercialrealty.com"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                        {t("contact.k18")} </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder={t("contact.k19")}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                        {t("contact.k20")} </label>
                      <select
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm transition-all bg-white"
                      >
                        <option value="General Support">{t("contact.k21")}</option>
                        <option value="Enterprise Sales">{t("contact.k22")}</option>
                        <option value="API Integration">{t("contact.k23")}</option>
                        <option value="Billing">{t("contact.k24")}</option>
                        <option value="Security / Privacy">{t("contact.k25")}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                      {t("contact.k26")} </label>
                    <textarea
                      rows={5}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder={t("contact.k27")}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm transition-all resize-none"
                    ></textarea>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <p className="text-xs text-gray-500">
                      {t("contact.k28")} </p>
                    <button
                      type="submit"
                      disabled={sending}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sending ? 'Transmitting...' : t("contact.k29")}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Quick Links */}
            <div className="mt-8 flex flex-wrap justify-between items-center gap-4 text-sm text-gray-500 px-2">
              <Link to="/faq" className="hover:text-blue-600 transition-colors">{t("contact.k30")}</Link>
              <Link to="/privacy-policy" className="hover:text-blue-600 transition-colors">{t("contact.k31")}</Link>
              <Link to="/terms-of-service" className="hover:text-blue-600 transition-colors">{t("contact.k32")}</Link>
              <Link to="/blog" className="hover:text-blue-600 transition-colors">{t("contact.k33")}</Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Contact;
